import { useSyncExternalStore } from "react";

export interface Toast {
  id: number;
  message: string;
  tone?: "default" | "success" | "warn" | "danger";
}

// Module-level toast store. We previously kept a `listeners` array and
// pushed/filtered into it from each Toaster mount. That worked in
// production but caused two subtle problems:
//   1. Under React 18 StrictMode (double-invoke in dev), each Toaster
//      mount briefly registered TWO listeners until the cleanup ran,
//      so toasts could double-render.
//   2. Concurrent rendering with multiple Toaster instances had no
//      guarantee about which listener saw what.
//
// useSyncExternalStore solves both: we expose a single get-snapshot
// function and a single subscribe function; React handles the rest.
// Toast() pushes into the queue and notifies all subscribers atomically.

let nextId = 1;
let queue: Toast[] = [];
const listeners = new Set<() => void>();

function getSnapshot(): Toast[] {
  return queue;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

function emit(): void {
  listeners.forEach((fn) => fn());
}

// Maximum number of concurrent toasts on screen. A burst of failures
// could otherwise spawn dozens of stacked toasts at once.
const MAX_VISIBLE = 5;

export function toast(message: string, tone: Toast["tone"] = "default"): void {
  const t: Toast = { id: nextId++, message, tone };
  queue = [...queue, t];
  // Drop the oldest if we're past the cap.
  if (queue.length > MAX_VISIBLE) queue = queue.slice(queue.length - MAX_VISIBLE);
  emit();
  setTimeout(() => {
    queue = queue.filter((x) => x.id !== t.id);
    emit();
  }, 3500);
}

export function Toaster() {
  const items = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return (
    <div className="pointer-events-none fixed bottom-5 right-5 z-50 flex w-80 flex-col gap-2">
      {items.map((t) => {
        const cls =
          t.tone === "success" ? "bg-[color:var(--color-success-soft)] text-[color:var(--color-success)] border-[color:var(--color-success)]"
          : t.tone === "warn" ? "bg-[color:var(--color-warn-soft)] text-[color:var(--color-warn)] border-[color:var(--color-warn)]"
          : t.tone === "danger" ? "bg-[color:var(--color-danger-soft)] text-[color:var(--color-danger)] border-[color:var(--color-danger)]"
          : "bg-[color:var(--color-surface)] text-[color:var(--color-ink)] border-[color:var(--color-border-strong)]";
        return (
          <div
            key={t.id}
            className={`pointer-events-auto rounded-xl border px-4 py-3 text-sm font-medium shadow-lg fade-in ${cls}`}
          >
            {t.message}
          </div>
        );
      })}
    </div>
  );
}
